<template>
    <v-container class="postage-rate-page" fluid>
        <v-card class="calculator-card" elevation="5">
            <form @submit.prevent="submitCalculation" novalidate>
                <div class="page-intro">
                    <p>
                        Whatever you need to send, we have a postal service to suit you.<br />
                        Calculate the cost of sending documents and parcels locally or internationally.
                    </p>
                </div>
                <fieldset
                    class="destination-fieldset"
                    :disabled="isLoading"
                    :aria-busy="isLoading ? 'true' : 'false'"
                >
                    <legend id="destinationLegend">Choose a destination</legend>

                    <div class="destination-options" role="radiogroup" aria-labelledby="destinationLegend">
                        <label v-for="option in destinationOptions"
                               :key="option.value"
                               class="destination-option"
                               :class="{ selected: destination === option.value }"
                               :for="`destination-${option.value}`">
                            <input :id="`destination-${option.value}`"
                                   v-model="destination"
                                   type="radio"
                                   name="destination"
                                   :value="option.value"
                                   required
                                   @change="handleDestinationChange" />

                            <span class="destination-icon" aria-hidden="true">
                                <img :src="option.image" alt="" class="destination-icon-img" />
                            </span>

                            <span class="destination-label">{{ option.label }}</span>
                        </label>
                    </div>
                </fieldset>

                <p>
                    <span class="red--text"><strong>* </strong></span>= Mandatory field
                </p>

                <v-autocomplete v-if="destination === 'foreign'"
                                id="destinationCountry"
                                v-model="selectedCountryCode"
                                :items="countryOptions"
                                item-text="name"
                                item-value="isoAlpha2Code"
                                label="Destination country"
                                placeholder="Start typing or select a destination country"
                                :loading="countriesLoading"
                                :disabled="isLoading || countriesLoading"
                                :aria-busy="countriesLoading ? 'true' : 'false'"
                                :rules="countryRules"
                                aria-required="true"
                                no-data-text="No countries found"
                                autocomplete="country-name"
                                required
                                clearable>
                    <template #label>
                        <span class="black--text">
                            <span class="red--text" aria-hidden="true">
                                <strong>* </strong>
                            </span>
                            Destination country:
                            <span class="sr-only"> (required)</span>
                        </span>
                    </template>
                </v-autocomplete>

                <p class="postcard-rate">
                    <strong>POSTCARD rate to all countries is &euro;{{ formatSettingAmount(settings.postageRateCalculatorPostCardValue) }}</strong>
                </p>

                <div class="calculator-fields">
                    <v-text-field id="weightInGrams"
                                  v-model="weightInGrams"
                                  type="number"
                                  :min="weightMin"
                                  :max="weightMax"
                                  step="1"
                                  inputmode="numeric"
                                  label="Weight in grams"
                                  aria-required="true"
                                  :rules="weightRules"
                                  :disabled="isLoading"
                                  autocomplete="off"
                                  required
                                  @wheel.native.prevent>
                        <template #label>
                            <span class="black--text">
                                <span class="red--text" aria-hidden="true">
                                    <strong>* </strong>
                                </span>
                                Weight in grams:
                                <span class="sr-only"> (required)</span>
                            </span>
                        </template>
                    </v-text-field>
                </div>

                <v-alert v-if="errorMessage"
                        type="error"
                        text
                        role="alert"
                        aria-live="polite"
                        class="calculation-alert">
                    {{ errorMessage }}
                </v-alert>

                <div class="actions">
                    <v-btn type="submit"
                           color="primary"
                           class="submit-button"
                           :loading="isLoading"
                           :disabled="isLoading || countriesLoading"
                           :aria-busy="isLoading ? 'true' : 'false'">
                        Get Rates
                    </v-btn>
                </div>

                <section v-if="showResults" class="results-section" aria-live="polite" aria-labelledby="postageRatesResultHeading">
                    <div class="results-header">
                        <div>
                            <h2 id="postageRatesResultHeading">
                                Postage Rates
                            </h2>

                            <dl class="result-summary">
                                <div>
                                    <dt>Destination:</dt>
                                    <dd>{{ resultCountryName || "-" }}</dd>
                                </div>
                                <div>
                                    <dt>Weight:</dt>
                                    <dd>{{ resultWeight || "-" }} grams</dd>
                                </div>
                            </dl>
                        </div>
                    </div>

                    <v-alert v-if="!rateRows.length"
                             type="info"
                             text
                             role="status"
                             class="calculation-alert">
                        No postage rates were returned for the selected details.
                    </v-alert>

                    <div v-else>
                        <div class="service-type-guidance">
                            <p v-for="(line, index) in serviceTypeGuidance" :key="index">
                                {{ line }}
                            </p>
                        </div>

                        <div class="results-table-wrapper">
                            <table class="results-table">
                            <caption>
                                Postage rates by service
                            </caption>

                            <thead>
                                <tr>
                                    <th scope="col">Service Type</th>
                                    <th scope="col">Trackable</th>
                                    <th scope="col">Delivery Aim</th>
                                    <th scope="col">Rate (€)</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="(row, index) in rateRows"
                                    :key="row.code || `${row.description}-${row.price}-${index}`">
                                    <td>{{ row.description }}</td>
                                    <td>{{ row.trackable ? "Yes" : "No" }}</td>
                                    <td>{{ row.deliveryInfo }}</td>
                                    <td>{{ formatPrice(row.price) }}</td>
                                </tr>
                            </tbody>
                            </table>
                        </div>

                        <v-expansion-panels class="mobile-rates-list" accordion>
                            <v-expansion-panel
                                v-for="(row, index) in rateRows"
                                :key="row.code || `${row.description}-${row.price}-${index}`">
                                <v-expansion-panel-header>
                                    <span class="mobile-rate-title">{{ row.description }}</span>
                                    <span class="mobile-rate-price">{{ formatPrice(row.price) }}</span>
                                </v-expansion-panel-header>

                                <v-expansion-panel-content>
                                    <dl class="mobile-rate-details">
                                        <div>
                                            <dt>Service Type</dt>
                                            <dd>{{ row.description }}</dd>
                                        </div>
                                        <div>
                                            <dt>Trackable</dt>
                                            <dd>{{ row.trackable ? "Yes" : "No" }}</dd>
                                        </div>
                                        <div>
                                            <dt>Delivery Aim</dt>
                                            <dd>{{ row.deliveryInfo }}</dd>
                                        </div>
                                        <div>
                                            <dt>Rate</dt>
                                            <dd>{{ formatPrice(row.price) }}</dd>
                                        </div>
                                    </dl>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>
                </section>
            </form>

            <section v-if="destination === 'foreign'" class="postal-service-info" aria-labelledby="postalServiceInfoHeading">
                <h2>Declaration for Items to be Sent to Foreign Countries</h2>

                <p class="declaration-instructions">
                    <strong>If you are sending an item of value, excluding letters, documents, and cards, please note that you will need to fill the online declaration.</strong>
                </p>

                <p class="declaration-instructions">
                    Follow the instructions at
                    <a :href="safeExternalUrl(settings.postageRateCalculatorSendYourItemLink)"
                       target="_blank"
                       rel="noopener"
                       aria-label="MaltaPost send your item instructions (opens in a new tab)">
                        this link <span class="sr-only"> (opens in a new tab)</span>
                    </a>
                    before delivering your items at the post office.
                </p>

                <h2 id="postalServiceInfoHeading" class="all-products-heading">All Products</h2>

                <h2>Documents and Goods Untracked (Foreign Post Standard)</h2>

                <p>
                    MaltaPost operates an outbound letter-mail service to more than 190 countries worldwide. Mail sent to the following countries is dispatched to its destination on the next working day: Australia, Austria, Belgium, Brazil, Bulgaria, Canada, China, Croatia, Cyprus, Czech Republic, Denmark, Estonia, Finland, France, Germany, Greece, Hong Kong, Hungary, Iceland, India, Ireland, Israel, Italy, Japan, Latvia, Lithuania, Luxembourg, New Zealand, Norway, Poland, Portugal, Romania, Russia, Slovakia, Slovenia, Spain, Sweden, Switzerland, The Netherlands, United States of America, United Kingdom and Vatican City.
                </p>

                <h2>Documents Registered (Foreign Post Registered)</h2>

                <p>
                    The Registered Mail service gives you extra peace of mind that the items have reached their destination. When registering your mail, MaltaPost will request a signature from the addressee as proof that your item has been successfully delivered.
                </p>

                <h2>Documents Advice of Receipt (Additional Service for Registered items)</h2>

                <p>
                    This is an added service complementing Registered Mail; you may choose to receive the signature indicating proof of delivery, which is sent to you by ordinary mail.
                </p>

                <h2>Goods Tracked (Foreign Parcel Post)</h2>

                <p>
                    This is a reliable and cost-effective way of sending parcels anywhere in the world. This service is ideal if you need to send a parcel to a foreign country and you are more concerned with keeping costs low rather than speed of delivery. With MaltaPost's efficient Parcel Post service, you can send items up to 20 kg to a wide variety of destinations. The following countries accept items up to 30 kg: Albania, Austria, Bulgaria, Canada, Croatia, Cyprus, Czech Republic, Denmark, Ethiopia, Finland, France, Monaco, Germany, Ghana, United Kingdom, Iceland, Irish Republic, Italy, Luxembourg, Macedonia, Malaysia, Norway, Portugal, Romania, Serbia and Montenegro, Slovakia, Slovenia, Switzerland, Syria, Tunisia and Ukraine.
                </p>

                <h2>EMS (Expedited Mail Service)</h2>

                <p>
                    This service includes the pickup of your item from your home or office and is currently available to 12 countries. This service is available from all Post Offices or call on 2122 4421 before 16:00 for a free pickup. For Terms and Conditions please
                    <a :href="safeExternalUrl(settings.postageRateCalculatorEmsInternationalCourierLink)"
                       target="_blank"
                       rel="noopener"
                       aria-label="Read the international courier terms and conditions (opens in a new tab)">click here<span class="sr-only"> (opens in a new tab)</span></a>.
                </p>

                <h2>International Courier (MaltaPost Express International - MEI)</h2>

                <p>
                    If you need to send documents or a parcel as quickly as possible, MaltaPost's Express International courier service has been designed to put time on your side.
                </p>

                <p>
                    This service promises the fastest way to get your package wherever you want it to go, with coverage in over 200 countries worldwide. It is also extremely secure, giving you peace of mind and proof of delivery once it reaches its destination. This service is available from all Post Offices or call on 2122 4421 before 16:00 for a free pickup.
                </p>

                <p>
                    A surcharge of &euro;{{ formatSettingAmount(settings.postageRateCalculatorSurchargeValue) }} is
                    applicable for parcels.
                </p>

                <p>
                    <a :href="safeExternalUrl(settings.postageRateCalculatorSearchLetterBoxOutletsLink)"
                       target="_blank"
                       rel="noopener"
                       aria-label="Find your nearest Letter Box, Stamp Vendor, Post Office and Sub Post Office (opens in a new tab)">
                        Click here to find your nearest Letter Box, Stamp Vendor, Post Office and Sub Post Office.<span class="sr-only"> (opens in a new tab)</span>
                    </a>
                </p>

                <h2>Note: * Delivery Aim</h2>

                <p>
                    This indicates the estimated time period taken for us to deliver a postal article to a recipient found in a foreign country and should only be used for guidance. Different time periods may apply depending on the foreign postal provider ultimately delivering the postal article. Even though we endeavour to deliver a postal article within the time period indicated, we cannot guarantee the actual delivery time period and the recipient may experience delays which are outside of our control.
                </p>
            </section>

            <section v-if="destination === 'local'" class="postal-service-info" aria-labelledby="localPostalServiceInfoHeading">
                <h2 id="localPostalServiceInfoHeading" class="all-products-heading">All Products</h2>

                <h2>Documents</h2>

                <p>
                    Our standard, cost-effective service for sending paper-based correspondence, personal mail and legal or business documentation between Malta and Gozo. This service is non-trackable and relies on traditional letterbox delivery.
                </p>

                <h2>Documents Registered</h2>

                <p>
                    Our secure, signature-backed delivery service for sending important documents, high-value paperwork, or small items between Malta and Gozo. Local Registered Mail provides end-to-end tracking and requires a physical signature upon delivery for absolute peace of mind. Maximum weight 2 kg.
                </p>

                <h2>Documents Registered (Registered Advice of Receipt)</h2>

                <p>
                    This is an added service complementing Registered Mail; you may choose to receive the signature indicating proof of delivery, which is sent to you by tracked mail.
                </p>

                <h2>Goods</h2>

                <p>
                    Our heavy-duty, reliable solution for shipping bulkier items, larger boxes, and heavy packages between Malta and Gozo. Designed for items weighing between 2 kg and 20 kg, Local Parcel Postage ensures your larger consignments are transported safely across our domestic network with standard tracking included.
                </p>

                <h2>Courier Delivery</h2>

                <p>
                    Our premium, ultra-fast point-to-point delivery solution for your most time-sensitive consignments across the Maltese Islands. Whether it's an urgent legal document, a critical business part, or a rapid e-commerce fulfilment, our dedicated courier fleet offers on-demand collection and delivery with tailored speed options to meet strict deadlines.
                </p>

                <p>
                    Please note that Express Courier Service (3-hour delivery time) is subject to availability, please contact us on 2596 1720 to book the service.
                </p>
            </section>
        </v-card>

    </v-container>
</template>

<script>
    import axios from "axios";

    const LOCAL_COUNTRY_CODE = "MT";
    const WEIGHT_MIN = 1;
    const WEIGHT_MAX = 30000;

    const FOREIGN_SERVICE_TYPE_GUIDANCE = Object.freeze([
        'From the list below:',
        'Find "Documents" for paper communications up to a weight of 1000 g.',
        'Find "Goods" for any tangible, physical object exceeding 1000 g in weight.'
    ]);

    const LOCAL_SERVICE_TYPE_GUIDANCE = Object.freeze([
        'From the list below:',
        'Find "Documents" for flat documents, small items and thick documents under 2 kg.',
        'Find "Goods" for larger, heavier boxes up to 20 kg.',
        'Find "Courier" for our premium express service.'
    ]);

    const destinationOptions = Object.freeze([
        { label: "Local", value: "local", image: require("@/assets/images/local_icon.svg") },
        { label: "Foreign", value: "foreign", image: require("@/assets/images/foreign_icon.svg") }
    ]);

    export default {
        name: "PostageRateCalculator",

        data() {
            return {
                destination: "local",
                destinationOptions,
                settings: {},
                countries: [],
                selectedCountryCode: "",
                weightInGrams: "",
                weightMin: WEIGHT_MIN,
                weightMax: WEIGHT_MAX,
                isLoading: false,
                countriesLoading: false,
                errorMessage: "",
                showResults: false,
                rateRows: [],
                resultCountryName: "",
                resultWeight: null,
                resultIsLocal: false,
                countryRules: [
                    value => this.destination !== "foreign" || !!value || "Destination country is required."
                ],
                weightRules: [
                    value => !!value || "Weight is required.",
                    value => Number(value) >= WEIGHT_MIN || `Weight must be at least ${WEIGHT_MIN} grams.`,
                    value => Number(value) <= WEIGHT_MAX || `Weight cannot exceed ${WEIGHT_MAX} grams.`,
                    value => Number.isInteger(Number(value)) || "Weight must be a whole number."
                ]
            };
        },

        computed: {
            countryOptions() {
                return this.countries
                    .map(country => ({
                        id: country.id,
                        name: country.name,
                        isoAlpha2Code: country.isO_Alpha2Code
                    }))
                    .filter(country => country.name
                        && country.isoAlpha2Code
                        && country.isoAlpha2Code.toUpperCase() !== LOCAL_COUNTRY_CODE)
                    .sort((first, second) => first.name.localeCompare(second.name));
            },

            selectedRequestCountryCode() {
                return this.destination === "local"
                    ? LOCAL_COUNTRY_CODE
                    : this.selectedCountryCode;
            },

            serviceTypeGuidance() {
                return this.resultIsLocal
                    ? LOCAL_SERVICE_TYPE_GUIDANCE
                    : FOREIGN_SERVICE_TYPE_GUIDANCE;
            }
        },

        watch: {
            destination() {
                this.hideResults();
            },

            selectedCountryCode() {
                this.hideResults();
            },

            weightInGrams() {
                this.hideResults();
            }
        },

        async mounted() {
            await Promise.all([
                this.loadSettings(),
                this.loadCountries()
            ]);
        },

        methods: {
            async loadSettings() {
                this.errorMessage = "";

                try {
                    const response = await axios.get("/Settings");

                    this.settings = response.data || {};
                } catch (error) {
                    this.errorMessage = "Unable to load calculator settings. Please try again later.";
                }
            },

            async loadCountries() {
                this.countriesLoading = true;
                this.errorMessage = "";

                try {
                    const response = await axios.get("/Countries");

                    this.countries = response.data || [];
                } catch (error) {
                    this.errorMessage = "Unable to load destination countries. Please try again later.";
                } finally {
                    this.countriesLoading = false;
                }
            },

            handleDestinationChange() {
                this.errorMessage = "";

                if (this.destination === "local") {
                    this.selectedCountryCode = "";
                }
            },

            hideResults() {
                this.showResults = false;
                this.rateRows = [];
                this.resultCountryName = "";
                this.resultWeight = null;
                this.resultIsLocal = false;
            },

            async submitCalculation() {
                this.errorMessage = "";
                this.showResults = false;

                const validationError = this.getValidationError();

                if (validationError) {
                    this.errorMessage = validationError;
                    return;
                }

                this.isLoading = true;

                try {
                    const response = await axios.get("/PostageRates", {
                        params: {
                            countryCode: this.selectedRequestCountryCode,
                            weight: Number(this.weightInGrams)
                        }
                    });

                    this.rateRows = response.data;

                    this.resultCountryName = this.getSelectedCountryName();
                    this.resultWeight = Number(this.weightInGrams);
                    this.resultIsLocal = this.selectedRequestCountryCode === LOCAL_COUNTRY_CODE;
                    this.showResults = true;
                } catch (error) {
                    this.errorMessage = this.getErrorMessage(error);
                } finally {
                    this.isLoading = false;
                }
            },

            getValidationError() {
                if (!this.destination) {
                    return "Destination is required.";
                }

                if (this.destination === "foreign" && !this.selectedCountryCode) {
                    return "Destination country is required.";
                }

                const weight = Number(this.weightInGrams);

                if (!Number.isInteger(weight) || weight < WEIGHT_MIN) {
                    return `Weight must be a whole number of at least ${WEIGHT_MIN} grams.`;
                }

                if (weight > WEIGHT_MAX) {
                    return `Weight cannot exceed ${WEIGHT_MAX} grams.`;
                }

                return "";
            },

            safeExternalUrl(value) {
                if (!value) {
                    return "#";
                }

                const trimmed = String(value).trim();
                const normalized = trimmed.toLowerCase();

                return normalized.startsWith("http://") || normalized.startsWith("https://")
                    ? trimmed
                    : "#";
            },

            getErrorMessage(error) {
                const responseData = error && error.response
                    ? error.response.data
                    : null;

                if (responseData && responseData.message) {
                    return responseData.message;
                }

                if (typeof responseData === "string" && responseData.trim()) {
                    return responseData;
                }

                return "Unable to calculate postage rates at this time. Please try again later.";
            },

            getSelectedCountryName() {
                if (this.destination === "local") {
                    return "Malta (Local)";
                }

                const country = this.countryOptions.find(
                    country => country.isoAlpha2Code === this.selectedCountryCode
                );

                const countryName = country ? country.name : this.selectedCountryCode;

                return `${countryName} (Foreign)`;
            },

            formatPrice(value) {
                const num = Number(value);
                return `\u20ac${isNaN(num) ? "0.00" : num.toFixed(2)}`;
            },

            formatSettingAmount(value) {
                if (value === null || value === undefined || value === "") {
                    return "";
                }

                return Number(value).toFixed(2);
            }
        }
    };
</script>

<style scoped>
    .postage-rate-page {
        width: 100%;
        max-width: 1120px;
        margin: 0 auto;
        padding-top: 32px;
        padding-bottom: 32px;
    }

    .calculator-card {
        width: 100%;
        padding: 32px;
        border-radius: 8px;
    }

    .results-header h2 {
        margin: 0 0 12px;
        color: #d43b2c;
        font-size: 28px;
        line-height: 1.25;
        letter-spacing: 0;
    }

        .results-header h2:focus {
            outline: 3px solid rgba(212, 59, 44, .35);
            outline-offset: 4px;
        }

    .postal-service-info {
        margin-top: 40px;
        padding-top: 32px;
        border-top: 1px solid rgba(0, 0, 0, .14);
        color: #000000;
    }

    .postal-service-info h2 {
        margin: 28px 0 10px;
        color: #d43b2c;
        font-size: 22px;
        line-height: 1.25;
        letter-spacing: 0;
    }

    .postal-service-info h2:first-child {
        margin-top: 0;
    }

    .postal-service-info h2.all-products-heading {
        font-size: 28px;
    }

    .postal-service-info p {
        max-width: 940px;
        margin: 0 0 16px;
        font-size: 16px;
        line-height: 1.55;
    }

    .postal-service-info a {
        color: #000000;
        text-decoration: underline;
    }

    .page-intro p,
    .result-summary {
        max-width: 760px;
        margin: 0;
        color: #000000;
        font-size: 18px;
        line-height: 1.4;
    }

    .page-intro p + p {
        margin-top: 16px;
    }

    .page-intro a {
        color: #000000;
        text-decoration: underline;
    }

    .result-summary {
        display: grid;
        grid-template-columns: max-content 1fr;
        column-gap: 8px;
        gap: 8px;
        align-items: baseline;
    }

    .result-summary div {
        display: contents;
        margin-top: 0;
    }

    .result-summary dt {
        margin: 0;
        font-weight: normal;
    }

    .result-summary dd {
        margin: 0;
        font-weight: normal;
    }

    .destination-fieldset {
        margin: 32px 0 24px;
        padding: 0;
        border: 0;
    }

        .destination-fieldset legend {
            margin-bottom: 18px;
            color: #000000;
            font-size: 22px;
            font-weight: bold;
            letter-spacing: 0;
        }

    .destination-options {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 18px;
    }

    .destination-option {
        min-height: 132px;
        display: flex;
        padding: 20px;
        cursor: pointer;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        border-radius: 8px;
        border: 1px solid rgba(0, 0, 0, .2);
        background: #ffffff;
        color: #000000;
    }

        .destination-option input {
            width: 1px;
            height: 1px;
            opacity: 0;
            position: absolute;
        }

        .destination-option:focus-within {
            outline: 3px solid rgba(212, 59, 44, .35);
            outline-offset: 2px;
        }

        .destination-option.selected {
            border-color: #d43b2c;
            box-shadow: 0 0 0 1px #d43b2c inset;
        }

    .destination-icon {
        display: block;
        line-height: 1;
    }

    .destination-icon-img {
        display: block;
        width: 85px;
        height: auto;
    }

    .destination-label {
        margin-top: 12px;
        font-size: 18px;
        font-weight: bold;
    }

    .postcard-rate {
        margin: 28px 0;
        padding-top: 22px;
        border-top: 1px solid rgba(0, 0, 0, .14);
        color: #000000;
        font-size: 17px;
    }

    .calculator-fields {
        max-width: 460px;
    }

    .calculation-alert {
        margin-top: 20px;
    }

    .actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 24px;
    }

    .submit-button {
        min-width: 132px;
    }

    .results-section {
        margin-top: 36px;
        padding-top: 28px;
        border-top: 1px solid rgba(0, 0, 0, .14);
    }

    .results-header {
        margin-bottom: 28px;
    }

    .service-type-guidance {
        margin-bottom: 20px;
        color: #000000;
    }

    .service-type-guidance p {
        margin: 0 0 8px;
        font-size: 15px;
        line-height: 1.5;
    }

    .service-type-guidance p:last-child {
        margin-bottom: 0;
    }

    .results-table-wrapper {
        width: 100%;
        overflow-x: auto;
    }

    .results-table {
        width: 100%;
        min-width: 760px;
        border-collapse: collapse;
    }

    .mobile-rates-list {
        display: none;
    }

    .mobile-rate-title {
        min-width: 0;
        flex: 1 1 auto;
        padding-right: 12px;
        color: #000000;
        font-size: 16px;
        font-weight: 600;
        overflow-wrap: anywhere;
    }

    .mobile-rate-price {
        flex: 0 0 auto;
        color: #d43b2c;
        font-size: 15px;
        font-weight: 600;
    }

    .mobile-rates-list ::v-deep .v-expansion-panel--active > .v-expansion-panel-header {
        background-color: rgba(212, 59, 44, 0.06);
        border-left: 3px solid #d43b2c;
    }

    .mobile-rate-details {
        margin: 0;
        color: #000000;
    }

    .mobile-rate-details div {
        display: grid;
        grid-template-columns: minmax(96px, 34%) minmax(0, 1fr);
        gap: 0;
        padding: 10px 0;
        border-top: 1px solid rgba(0, 0, 0, .12);
    }

    .mobile-rate-details dt {
        padding-right: 12px;
        border-right: 1px solid rgba(0, 0, 0, .18);
        font-weight: bold;
    }

    .mobile-rate-details dd {
        min-width: 0;
        margin: 0;
        padding-left: 12px;
        overflow-wrap: anywhere;
    }

        .results-table caption {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip-path: inset(50%);
            white-space: nowrap;
            border: 0;
        }

        .results-table th,
        .results-table td {
            padding: 14px 12px;
            text-align: left;
            vertical-align: top;
            border-bottom: 1px solid rgba(0, 0, 0, .14);
        }

        .results-table thead th {
            color: #000000;
            font-weight: bold;
            background: #e0e0e0;
        }

        .results-table tbody th {
            color: #000000;
            font-weight: bold;
        }

    @media screen and (max-width: 699px) {
        .postage-rate-page {
            padding: 16px 0 24px;
        }

        .calculator-card {
            padding: 22px 16px;
        }

        .results-header h2 {
            font-size: 24px;
        }

        .postal-service-info h2 {
            font-size: 20px;
        }

        .postal-service-info h2.all-products-heading {
            font-size: 24px;
        }

        .page-intro p,
        .result-summary,
        .destination-label {
            font-size: 16px;
        }

        .destination-options {
            grid-template-columns: 1fr;
        }

        .destination-option {
            min-height: 112px;
        }

        .actions {
            justify-content: stretch;
        }

        .submit-button {
            width: 100%;
        }

        .results-table-wrapper {
            display: none;
        }

        .mobile-rates-list {
            display: block;
        }

    }
</style>
